import React from 'react';
import { ChevronUp, ChevronDown, Zap, Clock, DollarSign, MessageSquare, Send, ArrowDownWideNarrow } from 'lucide-react';
import { Message } from '../../types/chat';

interface UsageStatsProps {
  usage: NonNullable<Message['metadata']>['usage'];
  isPartial?: boolean;
}

export const UsageStats: React.FC<UsageStatsProps> = ({ usage, isPartial }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  if (!usage) return null;

  return (
    <div className="mt-3 transform transition-all duration-200 ease-in-out">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full px-4 py-3 text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/50 hover:bg-gray-100 dark:hover:bg-gray-900/80 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 ease-in-out"
      >
        <div className="flex items-center space-x-2">
          <span className="font-semibold flex items-center gap-2">
            <Zap size={16} className="text-blue-500" />
            Usage Info
          </span>
          {isPartial && (
            <span className="flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-blue-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
            </span>
          )}
        </div>
        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      
      {isExpanded && (
        <div className="mt-2 p-5 bg-gray-50 dark:bg-gray-900/50 rounded-xl shadow-lg text-xs transform transition-all duration-200 ease-in-out">
          {/* Summary Section */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pb-5 border-b border-gray-200 dark:border-gray-700">
            <div className="space-y-1">
              <div className="text-gray-500 dark:text-gray-400 flex items-center gap-2">
                <MessageSquare size={14} />
                Total Tokens {isPartial && <span className="text-blue-500">(Estimating)</span>}
              </div>
              <div className="font-semibold text-gray-900 dark:text-white text-sm">
                {usage.total_tokens.toLocaleString()}
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-gray-500 dark:text-gray-400 flex items-center gap-2">
                <DollarSign size={14} />
                Total Cost {isPartial && <span className="text-blue-500">(Estimating)</span>}
              </div>
              <div className="font-semibold text-gray-900 dark:text-white text-sm">
                ${parseFloat(usage.total_price).toFixed(5)} {usage.currency}
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-gray-500 dark:text-gray-400 flex items-center gap-2">
                <Clock size={14} />
                Latency {isPartial && <span className="text-blue-500">(In Progress)</span>}
              </div>
              <div className="font-semibold text-gray-900 dark:text-white text-sm">
                {usage.latency.toFixed(2)}s
              </div>
            </div>
          </div>

          {/* Detailed Pricing Section */}
          <div className="pt-5">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Prompt Section */}
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <Send size={14} className="text-blue-500" />
                  Prompt Details
                </h4>
                <div className="space-y-3 bg-white dark:bg-gray-800/50 p-4 rounded-lg shadow-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">Tokens</span>
                    <span className="font-medium text-gray-900 dark:text-white">{usage.prompt_tokens.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">Price</span>
                    <span className="font-medium text-gray-900 dark:text-white">${usage.prompt_price}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">Price per Token</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      ${(parseFloat(usage.prompt_price) / usage.prompt_tokens).toFixed(8)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Completion Section */}
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <ArrowDownWideNarrow size={14} className="text-green-500" />
                  Completion Details
                </h4>
                <div className="space-y-3 bg-white dark:bg-gray-800/50 p-4 rounded-lg shadow-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">Tokens</span>
                    <span className="font-medium text-gray-900 dark:text-white">{usage.completion_tokens.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">Price</span>
                    <span className="font-medium text-gray-900 dark:text-white">${usage.completion_price}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">Price per Token</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      ${(parseFloat(usage.completion_price) / usage.completion_tokens).toFixed(8)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
